import { toast } from 'sonner'
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'
import { uploadFileToRepo } from '~/lib/file-utils'
import { FileNode } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/comments/components/editor/extensions/file'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'

const uploadKey = new PluginKey('upload-file')

export const UploadFilesPlugin = () =>
  new Plugin({
    key: uploadKey,
    state: {
      init() {
        return DecorationSet.empty
      },
      apply(tr, set) {
        set = set.map(tr.mapping, tr.doc)
        // See if the transaction adds or removes any placeholders
        const action = tr.getMeta(uploadKey)
        if (action && action.add) {
          const { id, pos, name } = action.add
          const nodeWrapper = document.createElement('div')
          nodeWrapper.setAttribute('class', 'react-renderer rt-file-upload')
          const placeholder = document.createElement('div')
          placeholder.setAttribute(
            'class',
            'flex items-center gap-2 bg-transparent border border-dark-border-solid rounded px-2 py-1 opacity-40 animate-pulse mb-2'
          )

          const text = document.createElement('span')
          text.setAttribute('class', 'text-sm text-dark-bright truncate')
          text.textContent = `Uploading ${name}...`

          placeholder.appendChild(text)
          nodeWrapper.appendChild(placeholder)

          const deco = Decoration.widget(pos, nodeWrapper, { id })
          set = set.add(tr.doc, [deco])
        } else if (action && action.remove) {
          set = set.remove(
            set.find(
              undefined,
              undefined,
              (spec) => spec.id == action.remove.id
            )
          )
        }
        return set
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })

function findPlaceholder(state: EditorState, id: object) {
  const decos = uploadKey.getState(state)
  const found = decos.find(null, null, (spec: { id: object }) => spec.id === id)
  return found.length ? found[0].from : null
}

export function startFileUpload(
  file: File,
  view: EditorView,
  pos: number,
  userData: { repoId: string; teamId: string }
) {
  // check if the file is supported
  if (file.type !== 'application/pdf' && file.type !== 'text/csv') {
    toast.error('File type not supported. Only PDF and CSV files are allowed.')
    return
  }

  // A fresh object to act as the ID for this upload
  const id = {}

  // Replace the selection with a placeholder
  const tr = view.state.tr
  if (!tr.selection.empty) tr.deleteSelection()

  const $pos = tr.doc.resolve(pos)
  const parent = $pos.parent

  let isWhitespaceOnlyParagraph = false

  if (parent.type.name === 'paragraph') {
    let isWhitespaceOnly = true
    parent.content.forEach((node) => {
      if (
        !(
          (node.type.name === 'text' && /^\s*$/.test(node.text || '')) ||
          node.type.name === 'hard_break'
        )
      ) {
        isWhitespaceOnly = false
      }
    })
    isWhitespaceOnlyParagraph = isWhitespaceOnly
  }

  if (isWhitespaceOnlyParagraph) {
    const from = $pos.before()
    const to = $pos.after()
    tr.delete(from, to)
    pos = from // update the actual position
  }

  tr.setMeta(uploadKey, {
    add: {
      id,
      pos,
      name: file.name,
    },
  })

  view.dispatch(tr)

  handleFileUpload(file, userData)
    .then(({ uploadURL }) => {
      const { schema } = view.state
      const pos = findPlaceholder(view.state, id)
      if (pos == null) return

      if (!schema.nodes[FileNode.name]) return

      const node = schema.nodes[FileNode.name]!.create({
        src: uploadURL,
        name: file.name,
        mimeType: file.type,
      })

      const transaction = view.state.tr
        .setMeta(uploadKey, { remove: { id } })
        .replaceWith(pos, pos, node)

      view.dispatch(transaction)
    })
    .catch((error) => {
      console.error('File upload failed:', error)
      toast.error('File upload failed. Please try again.')
      setTimeout(() => {
        view.dispatch(view.state.tr.setMeta(uploadKey, { remove: { id } }))
      }, 500)
    })
}

export const handleFileUpload = async (
  file: File,
  { repoId, teamId }: { repoId: string; teamId: string }
): Promise<{
  uploadURL: string
}> => {
  try {
    const { uploadURL } = await uploadFileToRepo(file, {
      repoId,
      teamId,
      fileHash: await getFileHashSHA256(file),
      protectFile: false,
    })

    return { uploadURL }
  } catch (error) {
    console.error('File upload error:', error)
    throw error
  }
}
